import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Mail, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-emerald-700 hover:text-emerald-800 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
              <p className="text-gray-600">Delinam Foundation</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-gray-200 shadow-lg">
              <CardContent className="p-6 sm:p-8 lg:p-12">
                <div className="prose prose-gray max-w-none">
                  {/* Introduction */}
                  <p className="text-gray-700 leading-relaxed mb-8">
                    Welcome to the Delinam Foundation website ("Website"). By accessing or using our Website 
                    and services, you agree to the following Terms of Service. If you do not agree, please 
                    do not use this Website.
                  </p>

                  {/* Section 1 */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      1. About Delinam Foundation
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Delinam Foundation is a community-focused organization dedicated to empowering communities 
                      through education, opportunity, and sustainable development initiatives.
                    </p>
                  </div>

                  {/* Section 2 */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      2. Use of Website
                    </h2>
                    <p className="text-gray-700 mb-4">
                      You agree to use this Website only for lawful purposes. You may not:
                    </p>
                    <ul className="list-none space-y-2 ml-0">
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Attempt to gain unauthorized access to our systems</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Disrupt the Website or its services</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Use the Website for fraudulent or harmful purposes</span>
                      </li>
                    </ul>
                  </div>

                  {/* Section 3 */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      3. Donations
                    </h2>
                    <p className="text-gray-700 mb-4">
                      Donations made through our Website are processed securely via third-party payment 
                      providers (e.g., Stripe).
                    </p>
                    <ul className="list-none space-y-2 ml-0">
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Donations are voluntary and non-refundable unless otherwise stated</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Delinam Foundation reserves the right to use donated funds to support its programs and operations</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Donors are responsible for ensuring their payment details are accurate</span>
                      </li>
                    </ul>
                  </div>

                  {/* Section 4 */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      4. No Tax Advice
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Delinam Foundation does not provide tax advice. Please consult your tax advisor 
                      regarding donation tax treatment.
                    </p>
                  </div>

                  {/* Section 5 */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      5. Intellectual Property
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      All content on this Website (text, logos, images, graphics) belongs to Delinam Foundation 
                      unless otherwise stated. You may not reproduce or distribute content without written permission.
                    </p>
                  </div>

                  {/* Section 6 */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      6. Privacy
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Your use of this Website is also governed by our{' '}
                      <Link to="/privacy" className="text-emerald-700 hover:text-emerald-800 font-medium underline">
                        Privacy Policy
                      </Link>.
                    </p>
                  </div>

                  {/* Section 7 */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      7. Limitation of Liability
                    </h2>
                    <p className="text-gray-700 mb-4">
                      Delinam Foundation is not responsible for:
                    </p>
                    <ul className="list-none space-y-2 ml-0 mb-4">
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Website downtime</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Third-party service issues (e.g., Stripe, Mailchimp)</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Any indirect or incidental damages</span>
                      </li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed">
                      Use of this Website is at your own risk.
                    </p>
                  </div>

                  {/* Section 8 */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      8. Changes to These Terms
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      We may update these Terms at any time. Continued use of the Website means you accept 
                      the updated terms.
                    </p>
                  </div>

                  {/* Section 9 - Contact */}
                  <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 rounded-r-lg">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <Mail className="w-6 h-6 mr-2 text-emerald-600" />
                      9. Contact
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      For questions about these Terms, contact:
                    </p>
                    <a 
                      href="mailto:info@delinamfoundation.org"
                      className="text-emerald-700 hover:text-emerald-800 font-medium"
                    >
                      info@delinamfoundation.org
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
